import React, { useEffect, useMemo, useState } from 'react';
import { Alert, Button, Card, Input, InputNumber, Modal, Select, Space, Spin, Tooltip, Tag, Switch, message } from 'antd';
import { FilePdfOutlined } from '@ant-design/icons';
import StandardTable from '../../../components/common/StandardTable';
import { api, fetchSqlData } from '../../../services/api';
import { dccPdfStatsRefresh } from '../../../services/dccApi';
import { useAuth } from '../../../components/common/security/AuthContext';
import type { DccIndexTableCorrespondence } from '../../../types';
import dayjs from 'dayjs';
import { useNavigate } from 'react-router-dom';

interface DccCorrespondenceIndexTableProps {
  selectedFolder: string | null;
}

const DccCorrespondenceIndexTable: React.FC<DccCorrespondenceIndexTableProps> = ({ selectedFolder }) => {
  const navigate = useNavigate();
  const { token } = useAuth();
  const [rows, setRows] = useState<DccIndexTableCorrespondence[]>([]);
  const [analyzingRowId, setAnalyzingRowId] = useState<number | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [refreshing, setRefreshing] = useState(false);
  const [forceRefresh, setForceRefresh] = useState(false);
  const [refreshLimit, setRefreshLimit] = useState<number>(500);

  const [typeFilter, setTypeFilter] = useState<string | undefined>(undefined);
  const [refFilter, setRefFilter] = useState('');
  const [subjectFilter, setSubjectFilter] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('');
  const [transmittalFilter, setTransmittalFilter] = useState('');
  const [replyRequiredFilter, setReplyRequiredFilter] = useState<string | undefined>(undefined);

  const norm = (s: any) => String(s ?? '').toLowerCase();
  const splitTerms = (q: any) =>
    String(q ?? '')
      .toLowerCase()
      .trim()
      .split(/\s+/)
      .filter(Boolean);
  const matchesAllTerms = (value: any, query: any) => {
    const terms = splitTerms(query);
    if (!terms.length) return true;
    const haystack = norm(value);
    return terms.every((t) => haystack.includes(t));
  };

  const rootName = useMemo(() => {
    const f = String(selectedFolder || '').replace(/\\/g, '/').replace(/^\/+/, '');
    if (!f) return '';
    return (f.split('/')[0] || '').toLowerCase();
  }, [selectedFolder]);

  const isCorrespondencesFolder = rootName === 'correspondences' || rootName === 'correspondence';
  useEffect(() => {
    if (!selectedFolder || !isCorrespondencesFolder) {
      setRows([]);
      setError(null);
      return;
    }

    const load = async () => {
      try {
        setLoading(true);
        setError(null);
        const data = await fetchSqlData('dcc_indextable_correspondences');
        const arr: DccIndexTableCorrespondence[] = Array.isArray(data) ? data : data?.data || [];
        setRows(arr || []);
      } catch (e: any) {
        setError(e?.message || 'Failed to load correspondence rows.');
        setRows([]);
      } finally {
        setLoading(false);
      }
    };

    load();
  }, [selectedFolder, isCorrespondencesFolder]);

  const typeOptions = useMemo(
    () => [
      { value: 'incoming', label: 'incoming' },
      { value: 'outgoing', label: 'outgoing' },
    ],
    [],
  );

  const replyRequiredOptions = useMemo(
    () => [
      { value: 'true', label: 'Required' },
      { value: 'false', label: 'Not Required' },
    ],
    [],
  );

  const filteredRows = useMemo(() => {
    let out = rows || [];

    if (typeFilter) {
      out = out.filter((r) => String(r.type || '').toLowerCase() === typeFilter.toLowerCase());
    }
    if (replyRequiredFilter) {
      const want = replyRequiredFilter === 'true';
      out = out.filter((r) => Boolean(r.reply_required) === want);
    }
    if (refFilter) {
      out = out.filter((r) => matchesAllTerms(r.ref_num, refFilter));
    }
    if (subjectFilter) {
      out = out.filter((r) => matchesAllTerms(r.subject, subjectFilter));
    }
    if (categoryFilter) {
      out = out.filter((r) => matchesAllTerms(r.category, categoryFilter));
    }
    if (transmittalFilter) {
      out = out.filter((r) => matchesAllTerms(r.transmittal_no, transmittalFilter));
    }

    return out;
  }, [
    rows,
    typeFilter,
    refFilter,
    subjectFilter,
    categoryFilter,
    transmittalFilter,
    replyRequiredFilter,
  ]);

  const resetFilters = () => {
    setTypeFilter(undefined);
    setRefFilter('');
    setSubjectFilter('');
    setCategoryFilter('');
    setTransmittalFilter('');
    setReplyRequiredFilter(undefined);
  };

  const reload = async () => {
    if (!selectedFolder || !isCorrespondencesFolder) return;

    try {
      setLoading(true);
      setError(null);
      const data = await fetchSqlData('dcc_indextable_correspondences');
      const arr: DccIndexTableCorrespondence[] = Array.isArray(data) ? data : data?.data || [];
      setRows(arr || []);
    } catch (e: any) {
      setError(e?.message || 'Failed to load correspondence rows.');
      setRows([]);
    } finally {
      setLoading(false);
    }
  };

  const handleRefreshIndex = async () => {
    const f = String(selectedFolder || '').replace(/\\/g, '/').replace(/^\/+/, '');
    if (!f) return;

    Modal.confirm({
      title: 'Refresh Correspondences Index',
      content: (
        <Space direction="vertical" style={{ width: '100%' }}>
          <div>How many files to process (limit)?</div>
          <InputNumber
            style={{ width: '100%' }}
            min={1}
            max={20000}
            value={refreshLimit}
            onChange={(v) => setRefreshLimit(typeof v === 'number' && Number.isFinite(v) ? v : 500)}
          />
        </Space>
      ),
      okText: 'Refresh',
      cancelText: 'Cancel',
      onOk: async () => {
        try {
          setRefreshing(true);
          await api.post(
            '/dcc/index-tables/correspondences/refresh',
            {
              folder_prefix: f,
              limit: refreshLimit,
              force: forceRefresh,
            },
            { headers: { 'Content-Type': 'application/json' } },
          );
        } catch (e: any) {
          const msg = e?.response?.data?.error || e?.message || 'Refresh Index failed.';
          setError(msg);
        } finally {
          setRefreshing(false);
          await reload();
        }
      },
    });
  };

  const handleAnalyzePdf = async (record: DccIndexTableCorrespondence) => {
    const filePath = String(record?.letter_path || '').replace(/\\/g, '/').replace(/^\/+/, '');
    if (!filePath.toLowerCase().endsWith('.pdf')) {
      message.warning('Letter path bir PDF dosyası değil.');
      return;
    }
    const slashIdx = filePath.indexOf('/');
    const rootKey = slashIdx > 0 ? filePath.slice(0, slashIdx) : '';
    const relPath = slashIdx > 0 ? filePath.slice(slashIdx + 1) : '';
    if (!rootKey || !relPath) {
      message.warning('Letter path kök klasör içermiyor.');
      return;
    }
    if (!token) {
      message.error('No session');
      return;
    }
    try {
      setAnalyzingRowId(record.id);
      const result = await dccPdfStatsRefresh({ root: rootKey, path: relPath, recursive: false }, token);
      message.success(
        `PDF stats updated (written=${result.written ?? 0}, ${result.elapsed_ms ?? 0}ms)`
      );
    } catch (e: any) {
      message.error(e?.message || 'PDF stats analysis failed');
    } finally {
      setAnalyzingRowId(null);
    }
  };

  const columns: any[] = [
    {
      title: 'PDF',
      key: 'pdf_stats',
      width: 70,
      render: (_: any, record: DccIndexTableCorrespondence) => {
        const p = String(record?.letter_path || '');
        if (!p.toLowerCase().endsWith('.pdf')) return null;
        return (
          <Tooltip title="Bu dosyanın PDF istatistiklerini güncelle" placement="topLeft">
            <Button
              size="small"
              icon={<FilePdfOutlined />}
              loading={analyzingRowId === record.id}
              onClick={(e: React.MouseEvent) => {
                e.stopPropagation();
                handleAnalyzePdf(record);
              }}
            />
          </Tooltip>
        );
      },
    },
    {
      title: 'Date',
      dataIndex: 'date',
      key: 'date',
      width: 95,
      render: (v: string) => (v ? dayjs(v).format('YYYY-MM-DD') : ''),
    },
    {
      title: 'Type',
      dataIndex: 'type',
      key: 'type',
      width: 90,
      render: (v: any) => {
        const s = String(v ?? '');
        if (!s) return '';
        const color = s.toLowerCase() === 'incoming' ? 'blue' : 'green';
        return <Tag color={color}>{s}</Tag>;
      },
    },
    { title: 'Ref #', dataIndex: 'ref_num', key: 'ref_num', width: 160 },
    { title: 'Category', dataIndex: 'category', key: 'category', width: 90 },
    {
      title: 'Subject',
      dataIndex: 'subject',
      key: 'subject',
      width: 400,
      render: (v: any) => {
        const s = String(v ?? '');
        if (!s) return '';
        return (
          <Tooltip title={s} placement="topLeft">
            <span>{s}</span>
          </Tooltip>
        );
      },
    },
    { title: 'Transmittal #', dataIndex: 'transmittal_no', key: 'transmittal_no', width: 120 },
    {
      title: 'Reply?',
      dataIndex: 'reply_required',
      key: 'reply_required',
      width: 80,
      render: (v: any) => (v ? 'Yes' : 'No'),
    },
    {
      title: 'Replied At',
      dataIndex: 'replied_at',
      key: 'replied_at',
      width: 105,
      render: (v: string) => (v ? dayjs(v).format('YYYY-MM-DD') : ''),
    },
    { title: 'Replied With', dataIndex: 'replied_with', key: 'replied_with', width: 160 },
    {
      title: 'Letter Path',
      dataIndex: 'letter_path',
      key: 'letter_path',
      width: 180,
      render: (v: any) => {
        const s = String(v ?? '');
        if (!s) return '';
        return (
          <Tooltip title={s} placement="topLeft">
            <span>{s}</span>
          </Tooltip>
        );
      },
    },
    {
      title: 'Reply Path',
      dataIndex: 'reply_path',
      key: 'reply_path',
      width: 180,
      render: (v: any) => {
        const s = String(v ?? '');
        if (!s) return '';
        return (
          <Tooltip title={s} placement="topLeft">
            <span>{s}</span>
          </Tooltip>
        );
      },
    },
  ];

  if (!isCorrespondencesFolder) return null;

  return (
    <Card style={{ width: '100%', maxWidth: '100%' }}>
      <Space direction="vertical" size="middle" style={{ width: '100%' }}>
        <div
          style={{
            display: 'flex',
            flexWrap: 'wrap',
            alignItems: 'center',
            gap: 8,
          }}
        >
          <Space wrap size={8}>
            <Select
              placeholder="Type"
              allowClear
              value={typeFilter}
              onChange={(v) => setTypeFilter(v || undefined)}
              options={typeOptions}
              style={{ width: 120 }}
              showSearch
              optionFilterProp="label"
            />
            <Input
              placeholder="Ref #"
              value={refFilter}
              onChange={(e) => setRefFilter(e.target.value)}
              allowClear
              style={{ width: 130 }}
            />
            <Input
              placeholder="Category"
              value={categoryFilter}
              onChange={(e) => setCategoryFilter(e.target.value)}
              allowClear
              style={{ width: 130 }}
            />
            <Input
              placeholder="Subject"
              value={subjectFilter}
              onChange={(e) => setSubjectFilter(e.target.value)}
              allowClear
              style={{ width: 180 }}
            />
            <Input
              placeholder="Transmittal #"
              value={transmittalFilter}
              onChange={(e) => setTransmittalFilter(e.target.value)}
              allowClear
              style={{ width: 140 }}
            />
            <Select
              placeholder="Reply?"
              allowClear
              value={replyRequiredFilter}
              onChange={(v) => setReplyRequiredFilter(v || undefined)}
              options={replyRequiredOptions}
              style={{ width: 130 }}
              showSearch
              optionFilterProp="label"
            />
          </Space>

          <Space wrap size={8} style={{ marginLeft: 'auto' }}>
            <Button onClick={resetFilters}>Reset</Button>
            <Button onClick={reload}>Reload</Button>
            <Space size={4}>
              <Switch checked={forceRefresh} onChange={setForceRefresh} />
              <span>Force</span>
            </Space>
            <Button type="primary" onClick={handleRefreshIndex} loading={refreshing}>
              Refresh Index
            </Button>
          </Space>
        </div>

        {error && (
          <Alert type="error" message="Failed to load correspondence rows" description={error} showIcon />
        )}

        <Spin spinning={loading}>
          <StandardTable
            columns={columns}
            dataSource={filteredRows}
            rowKey={(r: any) => String(r.id)}
            onRow={(record: any) => ({
              onClick: () => {
                const filePath = String(record?.letter_path || record?.file_path || '').replace(/\\/g, '/').replace(/^\/+/, '');
                if (!filePath) return;
                const parentPath = filePath.includes('/') ? filePath.slice(0, filePath.lastIndexOf('/')) : '';
                if (!parentPath) return;
                const qs = new URLSearchParams();
                qs.set('path', parentPath);
                const url = `/dcc/documents?${qs.toString()}`;
                window.open(url, '_blank', 'noopener,noreferrer');
              },
            })}
            size="small"
            style={{ width: '100%' }}
            scroll={{ x: 'max-content' }}
            pagination={{ pageSize: 20, showSizeChanger: true, pageSizeOptions: ['10', '20', '50', '100'] }}
          />
        </Spin>
      </Space>
    </Card>
  );
};

export default DccCorrespondenceIndexTable;
